import { useMemo } from "react";
import { numberToLocaleString } from "@/lib/utils";
import { PlayerDropsMessage } from "./PlayerDropsGenericSubcards";
import { playerDropCategories } from "@/lib/playerDropCategories";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type DisplayCategoryDatum = {
    label: string;
    tooltip: string;
    color: string;
    count: number;
}

type DrilldownOverviewSubcardProps = {
    dropTypes: [string, number][];
};

export default function DrilldownOverviewSubcard({ dropTypes }: DrilldownOverviewSubcardProps) {
    let { totalDrops, categories } = useMemo(() => {
        let totalDrops = 0;
        const categories: Record<string, DisplayCategoryDatum> = {};
        for (const [cat, cnt] of dropTypes) {
            totalDrops += cnt;
            const normalizedKey = cat.toLowerCase() === 'quit' ? 'player' : (cat.toLowerCase() === 'kick' ? 'resource' : cat.toLowerCase());
            if (!(normalizedKey in playerDropCategories)) continue;
            categories[normalizedKey] = {
                label: playerDropCategories[normalizedKey].label,
                tooltip: playerDropCategories[normalizedKey].description,
                color: playerDropCategories[normalizedKey].color,
                count: cnt,
            };
        }
        return {
            totalDrops,
            categories: Object.entries(categories),
        };
    }, [dropTypes]);

    if (!categories.length) {
        return <PlayerDropsMessage message="No player drops within this time window." />;
    }

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 w-full p-2">
            {categories.map(([reasonId, reasonData]) => {
                const percentage = totalDrops > 0 ? (reasonData.count / totalDrops) * 100 : 0;
                const glowShadow = {
                    boxShadow: `0 0 16px ${reasonData.color}06`,
                };

                return (
                    <Tooltip key={reasonId}>
                        <TooltipTrigger asChild>
                            <div
                                style={glowShadow}
                                className="bg-white/[0.01] border border-white/5 backdrop-blur-md rounded-xl p-4 flex flex-col gap-2 relative overflow-hidden group transition-all duration-300 hover:translate-y-[-2px] hover:bg-white/[0.02] cursor-help select-none"
                            >
                                {/* Radial glow overlay */}
                                <div 
                                    className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"
                                    style={{
                                        background: `radial-gradient(circle at top, ${reasonData.color}, transparent 60%)`
                                    }}
                                />

                                <div className="flex items-center justify-between z-10">
                                    <span className="font-mono text-[10px] font-bold tracking-widest text-slate-400 uppercase">
                                        {reasonData.label}
                                    </span>
                                    <span className="font-mono text-[10px] text-muted-foreground">
                                        {numberToLocaleString(percentage, 1)}%
                                    </span>
                                </div>

                                <div className="flex items-baseline gap-1 mt-1 z-10">
                                    <span className="text-2xl font-bold font-mono text-white leading-none">
                                        {numberToLocaleString(reasonData.count)}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground uppercase font-mono">
                                        drops
                                    </span>
                                </div>

                                {/* Progress bar */}
                                <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden mt-1.5 z-10">
                                    <div 
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{ 
                                            width: `${percentage}%`,
                                            backgroundColor: reasonData.color 
                                        }}
                                    />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-72 text-center bg-card border-white/10 p-3 shadow-lg">
                            <p className="font-semibold text-xs mb-1" style={{ color: reasonData.color }}>
                                {reasonData.label} Category
                            </p>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                                {reasonData.tooltip}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                );
            })}
        </div>
    );
}
